import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Box,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Chip,
    Grid,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    BugReport as BugIcon,
    Link as LinkIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon
} from '@mui/icons-material';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';

const CreateJiraBugDialog = ({ open, onClose, issue, onSuccess }) => {
    // Form state
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [boards, setBoards] = useState([]);
    const [selectedBoard, setSelectedBoard] = useState(null);
    const [sprints, setSprints] = useState([]);
    const [selectedSprint, setSelectedSprint] = useState(null);
    const [stories, setStories] = useState([]);
    const [selectedStory, setSelectedStory] = useState(null);
    
    // Override fields
    const [summary, setSummary] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('Medium');
    
    // Loading states
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [loadingBoards, setLoadingBoards] = useState(false);
    const [loadingSprints, setLoadingSprints] = useState(false);
    const [loadingStories, setLoadingStories] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    // Feedback
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Initialize form when dialog opens
    useEffect(() => {
        if (open && issue) {
            setSummary(issue.title || '');
            setDescription(issue.description || '');
            setPriority(issue.priority || 'Medium');
            setSelectedProject(null);
            setSelectedBoard(null);
            setSelectedSprint(null);
            setSelectedStory(null);
            setError(null);
            setSuccess(null);
            
            // Pre-select linked story if exists
            if (issue.jira_story_id) {
                setSelectedStory({ key: issue.jira_story_id, summary: 'Loading...' });
            }
            
            // Fetch projects
            fetchProjects();
        }
    }, [open, issue]);

    // Fetch projects on dialog open
    const fetchProjects = async () => {
        setLoadingProjects(true);
        setError(null);
        try {
            const response = await api.get('/issues/jira-projects');
            setProjects(response.data);
        } catch (err) {
            console.error('Failed to fetch projects:', err);
            if (err.response?.status === 401) {
                setError('JIRA account not connected. Please connect your JIRA account in Settings.');
            } else {
                setError('Failed to load JIRA projects. Please try again.');
            }
        } finally {
            setLoadingProjects(false);
        }
    };

    // Fetch boards when project is selected
    const fetchBoards = async (projectKey) => {
        setLoadingBoards(true);
        setBoards([]);
        setSprints([]);
        setSelectedBoard(null);
        setSelectedSprint(null);
        try {
            const response = await api.get(`/issues/jira-boards/${projectKey}`);
            setBoards(response.data);
            
            // Auto-select first Scrum board if available
            const scrumBoard = response.data.find(b => b.type === 'scrum');
            if (scrumBoard) {
                setSelectedBoard(scrumBoard);
                fetchSprints(scrumBoard.id);
            } else if (response.data.length > 0) {
                setSelectedBoard(response.data[0]);
                fetchSprints(response.data[0].id);
            }
        } catch (err) {
            console.error('Failed to fetch boards:', err);
        } finally {
            setLoadingBoards(false);
        }
    };

    // Fetch sprints when board is selected
    const fetchSprints = async (boardId) => {
        setLoadingSprints(true);
        setSprints([]);
        setSelectedSprint(null);
        try {
            const response = await api.get(`/issues/jira-sprints/${boardId}`);
            setSprints(response.data);
            
            // Auto-select active sprint if available
            const activeSprint = response.data.find(s => s.state === 'active');
            if (activeSprint) {
                setSelectedSprint(activeSprint);
            }
        } catch (err) {
            console.error('Failed to fetch sprints:', err);
        } finally {
            setLoadingSprints(false);
        }
    };

    // Search stories in project
    const fetchStories = async (projectKey, query = '') => {
        setLoadingStories(true);
        try {
            const response = await api.get(`/issues/jira-stories/${projectKey}`, {
                params: { query }
            });
            setStories(response.data);
        } catch (err) {
            console.error('Failed to fetch stories:', err);
        } finally {
            setLoadingStories(false);
        }
    };

    // Handle project selection
    const handleProjectChange = (event, newValue) => {
        setSelectedProject(newValue);
        if (newValue) {
            fetchBoards(newValue.key);
            fetchStories(newValue.key);
        } else {
            setBoards([]);
            setSprints([]);
            setStories([]);
        }
    };

    // Handle board selection
    const handleBoardChange = (event) => {
        const board = boards.find(b => b.id === event.target.value);
        setSelectedBoard(board);
        if (board) {
            fetchSprints(board.id);
        }
    };

    // Handle story search
    const handleStorySearch = (event, newInputValue) => {
        if (selectedProject && newInputValue.length >= 2) {
            fetchStories(selectedProject.key, newInputValue);
        }
    };

    // Parse screenshots from issue
    const getScreenshots = () => {
        if (!issue?.screenshot_urls) return [];
        return issue.screenshot_urls.split('\n').filter(url => url.trim());
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!selectedProject) {
            setError('Please select a JIRA project');
            return;
        }

        setSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            const payload = {
                project_key: selectedProject.key,
                sprint_id: selectedSprint?.id || null,
                link_to_story: selectedStory?.key || null,
                summary_override: summary !== issue.title ? summary : null,
                description_override: description !== issue.description ? description : null,
                priority_override: priority !== issue.priority ? priority : null
            };

            const response = await api.post(`/issues/${issue.id}/create-jira-bug`, payload);
            
            setSuccess(`Bug ${response.data.jira_bug_id} created successfully! ${response.data.attachments_uploaded} attachment(s) uploaded.`);
            
            // Call success callback after short delay
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess(response.data);
                }
                onClose();
            }, 2000);
            
        } catch (err) {
            console.error('Failed to create JIRA bug:', err);
            const errorMessage = err.response?.data?.detail || 'Failed to create JIRA bug. Please try again.';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const screenshots = getScreenshots();

    return (
        <Dialog 
            open={open} 
            onClose={submitting ? undefined : onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{ sx: { minHeight: '60vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugIcon color="error" />
                    <Typography variant="h6">Create JIRA Bug</Typography>
                </Box>
                <IconButton onClick={onClose} disabled={submitting}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Left Column - JIRA Settings */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            JIRA Settings
                        </Typography>
                        
                        {/* Project Selection */}
                        <Autocomplete
                            value={selectedProject}
                            onChange={handleProjectChange}
                            options={projects}
                            getOptionLabel={(option) => `${option.key} - ${option.name}`}
                            loading={loadingProjects}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Project *"
                                    placeholder="Select JIRA project"
                                    margin="normal"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingProjects && <CircularProgress size={20} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {option.key}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.name}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        />

                        {/* Board Selection (for sprints) */}
                        {boards.length > 0 && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Board</InputLabel>
                                <Select
                                    value={selectedBoard?.id || ''}
                                    onChange={handleBoardChange}
                                    label="Board"
                                    disabled={loadingBoards}
                                >
                                    {boards.map((board) => (
                                        <MenuItem key={board.id} value={board.id}>
                                            {board.name} ({board.type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Sprint Selection */}
                        {selectedBoard && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Sprint (Optional)</InputLabel>
                                <Select
                                    value={selectedSprint?.id || ''}
                                    onChange={(e) => {
                                        const sprint = sprints.find(s => s.id === e.target.value);
                                        setSelectedSprint(sprint || null);
                                    }}
                                    label="Sprint (Optional)"
                                    disabled={loadingSprints}
                                >
                                    <MenuItem value="">
                                        <em>No Sprint</em>
                                    </MenuItem>
                                    {sprints.map((sprint) => (
                                        <MenuItem key={sprint.id} value={sprint.id}>
                                            {sprint.name}
                                            {sprint.state === 'active' && (
                                                <Chip 
                                                    label="Active" 
                                                    size="small" 
                                                    color="success" 
                                                    sx={{ ml: 1 }} 
                                                />
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Link to Story */}
                        {selectedProject && (
                            <Autocomplete
                                value={selectedStory}
                                onChange={(event, newValue) => setSelectedStory(newValue)}
                                onInputChange={handleStorySearch}
                                options={stories}
                                getOptionLabel={(option) => `${option.key} - ${option.summary}`}
                                loading={loadingStories}
                                isOptionEqualToValue={(option, value) => option.key === value?.key}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Link to Story (Optional)"
                                        placeholder="Search stories..."
                                        margin="normal"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <LinkIcon color="action" sx={{ mr: 1 }} />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {loadingStories && <CircularProgress size={20} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {option.key}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {option.summary}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />
                        )}
                    </Grid>

                    {/* Right Column - Bug Details */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Bug Details
                        </Typography>

                        {/* Summary */}
                        <TextField
                            label="Summary"
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            fullWidth
                            margin="normal"
                            required
                        />

                        {/* Priority */}
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                                label="Priority"
                            >
                                <MenuItem value="Critical">Critical → JIRA: Highest</MenuItem>
                                <MenuItem value="High">High → JIRA: High</MenuItem>
                                <MenuItem value="Medium">Medium → JIRA: Medium</MenuItem>
                                <MenuItem value="Low">Low → JIRA: Low</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Description - Rich Text Editor */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Description
                            </Typography>
                            <Box 
                                sx={{ 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    minHeight: 200,
                                    '& .rdw-editor-wrapper': {
                                        border: 'none'
                                    },
                                    '& .rdw-editor-toolbar': {
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: 'background.paper',
                                        borderRadius: '4px 4px 0 0',
                                        padding: '6px',
                                        marginBottom: 0
                                    },
                                    '& .rdw-editor-main': {
                                        padding: '12px',
                                        minHeight: 150,
                                        maxHeight: 300,
                                        overflow: 'auto'
                                    }
                                }}
                            >
                                <RichTextEditor
                                    value={description}
                                    onChange={(html) => setDescription(html)}
                                    placeholder="Describe the bug in detail..."
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* Media Preview */}
                    {(screenshots.length > 0 || issue?.video_url) && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Attachments to Upload
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                {screenshots.length > 0 && (
                                    <Chip
                                        icon={<ImageIcon />}
                                        label={`${screenshots.length} Screenshot(s)`}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {issue?.video_url && (
                                    <Chip
                                        icon={<VideoIcon />}
                                        label="1 Video Recording"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                These files will be uploaded as attachments to the JIRA bug
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    disabled={submitting || !selectedProject || !summary.trim()}
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <BugIcon />}
                >
                    {submitting ? 'Creating Bug...' : 'Create Bug in JIRA'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateJiraBugDialog;
